/**
 * BondAutonomyEditor — Frente B Plano 2 (setup) peça 4: EDITAR a autonomia de agenda
 * por-cliente direto na ficha da Pessoa (o display read-only chegou na Sessão 3).
 *
 * A autonomia mora no `BondParticipant` (override por-cliente, nível mais fino do cascade
 * Cliente▸Playbook▸Tenant▸plataforma). "IA agenda sozinha" (propose_and_book) marca um cliente
 * prioritário; "herda padrão" (null) limpa o override. Prioriza fila é ortogonal (ordenação ≠
 * autonomia). Só renderiza onde a pessoa é participant (tem participantId/version p/ o If-Match).
 */
import { useState } from 'react';

import { useToastStore } from '../../stores/toast-store';

import { useUpdateBondAutonomy } from './use-persons';

import type { JSX } from 'react';

/**
 * Modos de autonomia como cartões descritivos (não um <select> cru): cada modo diz o que a IA
 * faz, para o operador escolher com clareza. '' = herda o cascade (null no wire).
 */
const MODE_OPTIONS: { value: string; label: string; desc: string }[] = [
  {
    value: '',
    label: 'Herda o padrão',
    desc: 'Segue a configuração do playbook / tenant. Use quando o cliente não precisa de tratamento diferente.',
  },
  {
    value: 'propose_and_book',
    label: 'IA agenda sozinha',
    desc: 'A IA remaneja e confirma horários sem passar por humano. Cliente prioritário.',
  },
  {
    value: 'propose_only',
    label: 'IA propõe; humano confirma',
    desc: 'A IA sugere horários, mas a liberação final é do operador.',
  },
  {
    value: 'qualify_only',
    label: 'IA só qualifica',
    desc: 'A IA coleta contexto mas não oferece horários — o agendamento é todo humano.',
  },
];

export interface BondAutonomyEditorProps {
  personId: string;
  bondId: string;
  participantId: string;
  version: number;
  currentMode: string | null;
  currentPreferred: boolean | null;
}

export function BondAutonomyEditor({
  personId,
  bondId,
  participantId,
  version,
  currentMode,
  currentPreferred,
}: BondAutonomyEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState(currentMode ?? '');
  const [preferred, setPreferred] = useState(currentPreferred === true);
  const update = useUpdateBondAutonomy(personId);
  const toast = useToastStore((s) => s.push);

  const open = (): void => {
    setMode(currentMode ?? '');
    setPreferred(currentPreferred === true);
    setEditing(true);
  };

  const save = (): void => {
    if (update.isPending) return;
    update.mutate(
      {
        bondId,
        participantId,
        version,
        nativeSchedulingMode: mode === '' ? null : mode,
        waitlistPreferred: preferred,
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast({ variant: 'success', title: 'Autonomia de agenda atualizada', durationMs: 3000 });
        },
        onError: () => {
          toast({
            variant: 'error',
            title: 'Não foi possível atualizar a autonomia.',
            description: 'A ficha pode ter sido alterada — recarregue e tente novamente.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-label="Editar autonomia de agenda"
        onClick={open}
        style={{ alignSelf: 'flex-start' }}
      >
        Editar autonomia
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-md"
      role="radiogroup"
      aria-label="Autonomia de agenda por cliente"
      style={{ padding: 'var(--space-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', maxWidth: 520 }}
    >
      <div className="flex flex-col gap-2">
        {MODE_OPTIONS.map((o) => {
          const active = mode === o.value;
          return (
            <button
              key={o.value === '' ? 'inherit' : o.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={o.label}
              onClick={() => setMode(o.value)}
              className="flex items-start gap-2 rounded-md text-left"
              style={{
                padding: 'var(--space-3)',
                border: `1px solid ${active ? 'var(--brand)' : 'var(--border-default)'}`,
                background: active ? 'var(--brand-subtle)' : 'var(--bg-surface)',
              }}
            >
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  width: 16,
                  height: 16,
                  marginTop: 2,
                  flex: 'none',
                  border: `2px solid ${active ? 'var(--brand)' : 'var(--border-strong)'}`,
                  background: active
                    ? 'radial-gradient(circle, var(--brand) 42%, transparent 46%)'
                    : 'transparent',
                }}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold" style={{ color: 'var(--fg-1)' }}>
                  {o.label}
                </span>
                <span className="block text-xs" style={{ color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.45 }}>
                  {o.desc}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <label
        className="flex items-center gap-2 rounded-md"
        style={{ padding: 'var(--space-3)', border: '1px solid var(--border-default)', background: 'var(--bg-surface)' }}
      >
        <input
          type="checkbox"
          checked={preferred}
          onChange={(e) => setPreferred(e.target.checked)}
          aria-label="Prioriza fila de espera"
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium" style={{ color: 'var(--fg-1)' }}>
            Prioriza fila de espera
          </span>
          <span className="block text-xs" style={{ color: 'var(--fg-3)' }}>
            Quando uma vaga abre, este cliente é chamado antes na fila. Independente do modo de autonomia.
          </span>
        </span>
      </label>

      <div className="flex items-center gap-2">
        <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setEditing(false)}
          disabled={update.isPending}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
